import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { IBaseSection, IContent } from 'src/interface';
import { Element, Icon, RouterLink, Section } from 'src/components';
import TitleWrap from 'src/modules/shared/components/TitleWrap';
import { useTracking } from 'src/hooks/useTracking';
import { ContentList } from 'src/modules/content/components/list';
import { SECTION_ID } from 'src/constants';

interface IProps extends IBaseSection<IContent> {
    ContentItemComponent?: React.FunctionComponent<any>;
}

export function ContentTopicType4Section(props: IProps) {
    const {
        items = [],
        title = '',
        link = '',
        className = cn(
            'topic-section',
            'relative',
            'border-y-[6px] border-solid border-[#F5F6F7]',
            'mt-[24px] mb-[20px] p-[18px_20px_4px]',
            'ml-[-20px]',
            'w-[calc(100%+40px)]',
        ),
        id,
        displayTimeIn,
        position,
        tracking = `|type_01|${position}|${id}`,
    } = props;

    const trackingRef = useTracking(id, `imps_type_01|${position}`, {
        threshold: 0.5,
    });

    const scrollRef = useRef<HTMLElement>(null);
    const [isAtEnd, setIsAtEnd] = useState(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
        };

        el.addEventListener('scroll', handleScroll, { passive: true });

        return () => el.removeEventListener('scroll', handleScroll);
    }, []);

    if (!items.length) {
        return null;
    }

    return (
        <Section className={className} domRef={trackingRef}>
            <TitleWrap
                title={title?.indexOf('#') > -1 ? title.slice(1) : title}
                link={link}
                renderAs="h2"
                data-text={`${title}`}
                className={cn(
                    'relative',
                    'flex',
                    'w-full mb-[12px]',
                    'text-[20px] leading-[16px] font-[900] uppercase',
                    'before:w-[5px] before:relative before:bg-[#F05B2F]',
                    'before:inline-block before:mr-[8px]',
                )}
            />
            <ContentList
                domRef={scrollRef}
                sectionId={`${SECTION_ID.CONTENT_BY_TOPIC}`}
                items={items}
                tracking={tracking}
                displayTimeIn={displayTimeIn}
                hasLayoutPhoto={false}
                className={cn(
                    'flex gap-x-[12px]',
                    'overflow-x-auto',
                    '[scrollbar-width:none]',
                    '[&::-webkit-scrollbar]:hidden',
                    {
                        ['[mask-image:linear-gradient(270deg,#fff0,#fffc_16%,#fff_40%,#fff)]']:
                            !isAtEnd,
                    },
                )}
                ContentItemProps={{
                    lazyloadImage: false,
                    showDescription: true,
                    truncateDescription: true,
                    objEllipsisDescription: {
                        widthContainer: 228,
                        line: 5,
                    },
                }}
                ContentItemClass={{
                    CardClass: cn(
                        '!block !w-[270px] shrink-0',
                        '!mt-0 !pt-0',
                        'rounded-[4px]',
                        'overflow-hidden',
                        'border border-solid border-[#E5E5E5]',
                    ),
                    CardImageClass:
                        'is-large !w-full !pb-[66.88%] my-0 rounded-none',
                    CardHeaderClass: '!leading-[1.4]',
                    DescriptionClass: 'hidden',
                    CardContentClass: '!ml-0 p-[12px_20px_20px] flex flex-col',
                }}
            />
            <RouterLink
                to={link}
                className={cn(
                    'uppercase text-[14px] leading-[1] font-semibold',
                    'h-[36px]',
                    'flex items-center justify-center',
                    'mt-[16px]',
                )}
            >
                <Icon
                    icon="new-video-expand-regular"
                    className="text-[16px] mr-[8px]"
                />
                <span>Xem thêm</span>
            </RouterLink>
        </Section>
    );
}

export default React.memo(ContentTopicType4Section);
